import {
  useCareSnapshot,
  type CareSnapshot as Snapshot,
  type CareSnapshotWindow,
  type CareTagFrequency,
} from "@/lib/api/care";
import { useDevOverridesStore } from "@/lib/hooks/admin/use-dev-overrides";
import { REDACTED_LABEL } from "@/lib/redacted-display";
import { Segmented, Skeleton } from "@somethings-ui/web";
import { Sparkles } from "lucide-react";
import { DateTime } from "luxon";
import { useState } from "react";
import { CareCard, FieldLabel, Pill, formatCareDate } from "./care-primitives";
import { HelpTip } from "./presentation-review-card";
import { TrajectoryChart } from "./trajectory-chart";

const WINDOWS: { value: CareSnapshotWindow; label: string }[] = [
  { value: "7d", label: "7 day" },
  { value: "90d", label: "90 day" },
  { value: "all", label: "All" },
];

export function CareSnapshot({ menteeId }: { menteeId: string }) {
  const [window, setWindow] = useState<CareSnapshotWindow>("7d");
  const snapshot = useCareSnapshot(menteeId, window);

  return (
    <CareCard className="flex flex-col rounded-2xl p-0">
      <div className="flex items-center justify-between px-4 pt-6">
        <h2 className="font-heading text-base font-bold leading-6 tracking-[-0.4px] text-[#1f1e1d]">
          Care snapshot
        </h2>
        <HelpTip
          text={`Care Snapshot is generated from assessment history, risk/escalation history, presentation history, and theme extraction. Content should be used to inform coding decisions — it is not itself a clinical determination.${
            snapshot.data
              ? ` Last generated on ${formatCareDate(snapshot.data.generated_at)} — updates daily.`
              : ""
          }`}
        />
      </div>
      <div className="px-4 pt-4">
        <Segmented
          aria-label="Snapshot window"
          className="w-full justify-between rounded-full bg-[#f6f4ef] p-1 [&>button[aria-pressed=true]]:border-transparent [&>button[aria-pressed=true]]:bg-white [&>button[aria-pressed=true]]:shadow-[0_3px_6px_rgba(0,0,0,0.13)] [&>button]:flex-1 [&>button]:justify-center [&>button]:text-xs"
          options={WINDOWS}
          value={window}
          onChange={(value) => setWindow(value as CareSnapshotWindow)}
        />
      </div>
      {snapshot.isPending ? (
        <SnapshotSkeleton />
      ) : snapshot.data ? (
        <SnapshotBody snapshot={snapshot.data} />
      ) : (
        <p className="p-6 font-body text-sm text-states-error">
          Unable to load snapshot.
        </p>
      )}
    </CareCard>
  );
}

function SnapshotBody({ snapshot }: { snapshot: Snapshot }) {
  const hidePhi = useDevOverridesStore((state) => state.hidePhiPii) === true;
  const pending = snapshot.ai_status === "pending";
  return (
    <>
      <div className="flex flex-col gap-4 pb-4 pl-6 pr-4 pt-6">
        <p className="font-body text-xs leading-4 text-[#58534b]">
          Reporting Period:{" "}
          <span className="font-bold text-[#1f1e1d]">
            {formatPeriod(snapshot.period_start, snapshot.period_end)}
          </span>
        </p>
        <div className="grid grid-cols-2 divide-x divide-[#dfddd8]">
          <BigStat value={snapshot.engagement_days} label="engaged days" />
          <BigStat
            value={snapshot.messages_sent}
            label="messages sent"
            className="pl-4"
          />
        </div>
      </div>

      <Block title="Symptom trajectory">
        {pending ? (
          <Skeleton className="h-10" />
        ) : snapshot.symptom_trajectory ? (
          <p className="font-body text-sm leading-5 text-black">
            {hidePhi ? REDACTED_LABEL : snapshot.symptom_trajectory}
          </p>
        ) : null}
        <TrajectoryChart
          points={snapshot.trajectory}
          periodStart={snapshot.period_start}
          periodEnd={snapshot.period_end}
        />
      </Block>

      <Block title="Conversation content frequency">
        {pending ? (
          <TagsSkeleton />
        ) : (
          <>
            <TagGroup
              label="Symptoms"
              tags={snapshot.symptoms}
              total={snapshot.engagement_days}
            />
            <TagGroup
              label="Environmental factors"
              tags={snapshot.environmental_factors}
              total={snapshot.engagement_days}
            />
          </>
        )}
      </Block>

      <Block
        title={
          <span className="inline-flex items-center gap-2">
            <Sparkles className="size-4" aria-hidden />
            Conversation Summary
          </span>
        }
      >
        {pending ? (
          <div className="flex flex-col gap-2" aria-busy="true">
            <Skeleton className="h-4" />
            <Skeleton className="h-4" />
            <Skeleton className="h-4 w-2/3" />
          </div>
        ) : (
          <p className="font-body text-sm leading-5 text-black">
            {hidePhi
              ? REDACTED_LABEL
              : (snapshot.conversation_summary ??
                (snapshot.ai_status === "failed"
                  ? "Summary unavailable right now. Reopen the tab to retry."
                  : "No summary available for this window."))}
          </p>
        )}
      </Block>
    </>
  );
}

function formatPeriod(start: string, end: string) {
  const from = DateTime.fromISO(start);
  const to = DateTime.fromISO(end);
  const startFormat = from.year === to.year ? "LLL d" : "LLL d, yyyy";
  return `${from.toFormat(startFormat)} – ${to.toFormat("LLL d, yyyy")}`;
}

function BigStat({
  value,
  label,
  className = "",
}: {
  value: number;
  label: string;
  className?: string;
}) {
  return (
    <div className={className}>
      <p className="font-heading text-[32px] font-bold leading-10 tracking-[-0.32px] text-[#272421]">
        {value}
      </p>
      <FieldLabel>{label}</FieldLabel>
    </div>
  );
}

function Block({
  title,
  children,
}: {
  title: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-3 border-t border-[#dfddd8] p-6">
      <h3 className="font-heading text-base font-bold leading-6 tracking-[-0.4px] text-[#1f1e1d]">
        {title}
      </h3>
      {children}
    </div>
  );
}

function TagGroup({
  label,
  tags,
  total,
}: {
  label: string;
  tags: CareTagFrequency[];
  total: number;
}) {
  return (
    <div className="flex flex-col gap-2">
      <FieldLabel className="text-[#a0998d]">{label}</FieldLabel>
      {tags.length === 0 ? (
        <p className="font-body text-sm leading-5 text-[#58534b]">
          None detected.
        </p>
      ) : (
        <div className="flex flex-wrap gap-2">
          {tags.map((tag) => (
            <Pill key={tag.name} className="px-4">
              {tag.name} {tag.days}/{total}
            </Pill>
          ))}
        </div>
      )}
    </div>
  );
}

function TagsSkeleton() {
  return (
    <div className="flex flex-col gap-3" aria-busy="true">
      <Skeleton className="h-3 w-20" />
      <div className="flex flex-wrap gap-2">
        <Skeleton className="h-7 w-32 rounded-full" />
        <Skeleton className="h-7 w-24 rounded-full" />
        <Skeleton className="h-7 w-28 rounded-full" />
      </div>
      <Skeleton className="h-3 w-32" />
      <div className="flex flex-wrap gap-2">
        <Skeleton className="h-7 w-28 rounded-full" />
        <Skeleton className="h-7 w-36 rounded-full" />
      </div>
    </div>
  );
}

function SnapshotSkeleton() {
  return (
    <div className="flex flex-col gap-4 p-6" aria-busy="true">
      <Skeleton className="h-4 w-48" />
      <div className="grid grid-cols-2 gap-4">
        <Skeleton className="h-14" />
        <Skeleton className="h-14" />
      </div>
      <Skeleton className="h-32" />
      <Skeleton className="h-24" />
    </div>
  );
}
